import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import OpenInNew from '@material-ui/icons/OpenInNew';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';
import { useEffect, useRef, useState } from 'react';
import create, { UseStore } from 'zustand';

import { visitDrawerTabs } from '../components/coding-tool/coding-components';
import { EditDialog, EditStore } from '../components/trucode/edits';
import { fetchRequest } from './';
import { alertColor } from './alerts/alerts-model';
import { Text } from './controls';
import { formatDate } from './date-time';
import { Alert } from './domain';
import { FormApi, formApi } from './form';
import { truncateSpan, withTooltip } from './format';
import { socketRequest } from './socket';
import { truCode } from './trucode';
import { WorksheetResult, worksheetMessage } from './worksheet-service';

type AlertChipProps = {
  alert: Alert;
  api?: UseStore<AlertApi>;
  edits?: EditStore;
  readonly?: boolean;
  onMipsAlert?: (a: Alert) => void;
};

function alertToString(alert: Alert): string {
  if (!alert) {
    return 'Alert';
  }

  let { errorText, description, alertType, details, createDate } = alert;

  let result = (alert?.label || alertType) + ': ' + (description || '').replace(/(\r\n|\n|\r)/gm, ' ');

  if (errorText) {
    result += '\n\n' + errorText.replace(/(\r\n|\n|\r)/gm, ' ');
  }

  if (details) {
    //  console.log('alert details', details.tuples,  alert.details)
    const tuples = details.tuples;
    if (tuples?.length) {
      result += '\n\n MIPS Tuples:';
      tuples.forEach((v) => {
        result += '\n' + v.cptCode + ' - ' + v.description + ' Modifier' + (v.modifier || '');
      });
    }
    const dxCodes = details.dxCodes;
    if (dxCodes?.length) {
      result += '\n\n MIPS DX codes:\n' + dxCodes.join(', ');
    }

    const { macName, sourceData } = details;
    if (macName) {
      result += '\n\n' + macName;
    }

    if (sourceData) {
      result += '\n\n' + sourceData;
    }
  }

  if (createDate) {
    result += '\n\nCreate Date: ' + formatDate(createDate);
  }

  return result;
}

export function AlertChip(props: AlertChipProps) {
  const { api, alert, edits, onMipsAlert, readonly } = props;
  let { alertType, canOverride, overrideReason } = alert;

  const isTruCode = alertType === 'TRUCODEEDIT';
  let label = alertToString(alert);

  const onTruCodeEdits = () => {
    truCode.setState({ alert });
    if (edits) {
      edits.setState({ alert });
    }
  };
  let hasTuples = !!alert?.details?.tuples?.length;
  const onMIPsTuples = () => {
    if (onMipsAlert) {
      onMipsAlert(alert);
    }
  };

  const [, setResolved] = useState(alert.resolved || alert.overridden || alert.acknowledged);
  const [ack, setAck] = useState(alert.acknowledged);

  const onOverride = () => {
    api.setState({ openAlert: null });
    api.setState({ openAlert: alert });
  };

  const onAcknowledge = async () => {
    if (api) {
      await api.getState().acknowledgeAlert(alert);
      setResolved(alert.acknowledged);
    } else {
      alert.acknowledged = true;
    }
    setAck(alert.acknowledged);
  };

  const smallStyle = { width: '8px', height: '8px' };
  const name = alert.alertType;
  return (
    <Chip
      size="small"
      color="primary"
      style={{
        backgroundColor: alertColor(alert),
        fontSize: 12,
        pointerEvents: readonly ? 'none' : undefined,
      }}
      label={
        <Grid container direction="row" spacing={1} justify="space-around" alignItems="center" style={{ flexWrap: 'nowrap' }}>
          <Grid item style={{ paddingLeft: 11, paddingBottom: 5, color: 'white' }}>
            {truncateSpan(label, '265px', false, name + '-label')}
          </Grid>
          {!ack && !canOverride && (
            <Grid item>
              {withTooltip(
                'Acknowledge Alert',
                <IconButton id={name + '-acknowledge-btn'} name="acknowledge-alert" onClick={onAcknowledge} style={smallStyle}>
                  <RemoveRedEye fontSize="small" htmlColor="white" />
                </IconButton>
              )}
            </Grid>
          )}
          {isTruCode && (
            <Grid item>
              {withTooltip(
                'View Tru Code Edits',
                <IconButton id={name + '-edits-btn'} onClick={onTruCodeEdits} style={smallStyle}>
                  <OpenInNew fontSize="small" htmlColor="white" />
                </IconButton>
              )}
            </Grid>
          )}

          {hasTuples && (
            <Grid item>
              {withTooltip(
                'Select MIPS Tuples',
                <IconButton id={name + '-mips-btn'} onClick={onMIPsTuples} style={smallStyle}>
                  <OpenInNew fontSize="small" htmlColor="white" />
                </IconButton>
              )}
            </Grid>
          )}
          {overrideReason && (
            <Grid
              item
              style={{
                fontStyle: 'italic',
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 5,
                color: 'white',
              }}
            >
              {truncateSpan(overrideReason, '120px', false)}
            </Grid>
          )}
          {canOverride && (
            <Grid item>
              {withTooltip(
                overrideReason ? 'Edit Reason' : 'Override Alert',
                <IconButton id={name + '-override-btn'} onClick={onOverride} style={smallStyle}>
                  <CreateIcon fontSize="small" htmlColor="white" />
                </IconButton>
              )}
            </Grid>
          )}
        </Grid>
      }
    />
  );
}
type AlertListProps = {
  api: UseStore<AlertApi>;
  edits: EditStore;
  readonly?: boolean;
  onMipsAlert?: (a: Alert) => void;
};
export function AlertList(props: AlertListProps) {
  const { api, edits, onMipsAlert, readonly } = props;
  const alerts = api((s) => s.alerts);

  return (
    <>
      <Grid item container direction="row" spacing={1} justify="flex-start" alignItems="flex-start" style={{ width: '100%' }}>
        {alerts && alerts.length ? (
          alerts.map((v, i) => (
            <Grid item key={i} id="alerts">
              <AlertChip alert={v} api={api} edits={edits} onMipsAlert={onMipsAlert} readonly={readonly} />
            </Grid>
          ))
        ) : (
          <span style={{ paddingLeft: 6 }}>No Alerts</span>
        )}
      </Grid>
      <OverrideDialogue api={api} />
      <EditDialog api={edits} />
    </>
  );
}

type MessageResult = WorksheetResult<Alert[]>;
type AlertApiProps = {
  worksheetId?: string;
  alerts?: Alert[];
  deferConnection?: boolean;
};

type AlertApi = {
  alerts: Alert[];
  openAlert: Alert;
  openEdit(alert: Alert): void;
  acknowledgeAlert(alert: Alert): void;
  overrideAlert(alert: Alert, overrideReason: string): void;
  close(): void;
  connect(): void;
};

export type AlertStore = UseStore<AlertApi>;
const socketsEnabled = true;

type AlertResult = WorksheetResult<Alert>;
export const acknowledgeAlertRequest = fetchRequest<Alert, AlertResult>({
  method: 'post',
  body: (args: Alert) => worksheetMessage(args, 'acknowledgeAlert'),
});

export const overrideAlertRequest = fetchRequest<Alert, AlertResult>({
  method: 'post',
  body: (args: Alert) => worksheetMessage(args, 'overrideAlert'),
});

export function alertApi(props?: AlertApiProps): AlertStore {
  const { worksheetId, alerts, deferConnection } = props || {};

  let close = () => null;
  let connect = () => null;

  const api = create<AlertApi>((set, get) => ({
    alerts: alerts || [],
    openAlert: null,
    openEdit: (alert: Alert) => {
      visitDrawerTabs.setState({ index: 2 });
      truCode.setState({ alert });
    },
    acknowledgeAlert: async (alert: Alert) => {
      alert.acknowledged = true;
      const response = await acknowledgeAlertRequest(alert);
      console.log('acknowledgeAlert', response);
      set({ alerts: get().alerts.slice() });
    },
    overrideAlert: async (alert: Alert) => {
      alert.overridden = true;
      const { worksheetId, alertId, overrideReason } = alert;
      const response = await overrideAlertRequest({
        worksheetId,
        alertId,
        overrideReason,
      });
      console.log('overrideAlert', response);
      set({ alerts: get().alerts.slice() });
    },
    close: () => close(),
    connect: () => connect(),
  }));

  if (worksheetId && socketsEnabled) {
    const messages = socketRequest({
      worksheetId,
      deferConnection,
      onSuccess: (data: MessageResult) => {
        const { result } = data;
        if (result && data.action !== 'checkconnection') {
          // console.log('socket result', data);
          //TODO filter
          // api.setState({ alerts: result });
        }
      },
    });
    close = () => messages.getState().close();
    connect = () => messages.getState().connect();
  }

  return api;
}

type OverrideDialogueProps = {
  api: AlertStore;
};
export function OverrideDialogue(props: OverrideDialogueProps) {
  const { api } = props;

  const openAlert = api((s) => s.openAlert);
  const form: FormApi = useRef(
    formApi({
      valid: (v) => v.reason?.length >= 5,
      onSubmit: async (v) => {
        const a = api.getState().openAlert;
        if (a) {
          a.overrideReason = v.reason;
          await api.getState().overrideAlert(a, v.reason);
          api.setState({ openAlert: null });
        }
      },
      values: {
        reason: null,
      },
      fields: {
        reason: {
          id: 'override-reason',
          required: true,
          label: 'Reason (5 Characters or More)',
          multiline: true,
          style: { width: '100%' },
          rows: 3,
          onEnter: () => form.submit(),
        },
      },
    })
  ).current;

  useEffect(() => {
    if (openAlert?.overrideReason) {
      form.setValues({ reason: openAlert.overrideReason });
    }
  });
  return (
    <Dialog open={!!openAlert} maxWidth={false} style={{ zIndex: 1531 }}>
      <DialogTitle>Override Alert</DialogTitle>
      <DialogContent style={{ minWidth: 400 }}>
        <Text {...form.controls.reason} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => api.setState({ openAlert: null })} color="primary" variant="outlined">
          {' '}
          Cancel{' '}
        </Button>
        <Button id="override-submit-btn" onClick={() => form.submit()} color="primary" variant="contained">
          {' '}
          Submit{' '}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
